/**
 * Role service, backed by Firestore (`roles` collection).
 * Roles are global (orgId null) or org-scoped.
 */

import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { auth, db, orgIdFromEmail, isAdminUser } from '../../../../shared/firebase';

const fetchAllRoles = async () => {
  const snap = await getDocs(collection(db, 'roles'));
  const roles = [];
  snap.forEach((d) => roles.push(d.data()));
  return roles;
};

/**
 * Get roles relevant to the current user (global + user's org).
 * @returns {Promise<Array>} Array of roles
 */
export const getRolesForUser = async () => {
  try {
    const fbUser = auth.currentUser;
    const orgId = fbUser ? orgIdFromEmail(fbUser.email) : null;
    const roles = await fetchAllRoles();
    return roles.filter((r) => !r.orgId || r.orgId === orgId);
  } catch (error) {
    console.error('Error fetching roles:', error);
    throw error;
  }
};

/**
 * Get all roles for role setup screens (admins see every org's roles).
 * @returns {Promise<Array>} Array of roles
 */
export const getRolesForRoleSetup = async () => {
  try {
    if (isAdminUser(auth.currentUser)) {
      return fetchAllRoles();
    }
    return getRolesForUser();
  } catch (error) {
    console.error('Error fetching roles:', error);
    throw error;
  }
};

/**
 * Get role by ID
 * @returns {Promise<Object>} Role object
 */
export const getRoleById = async (roleId) => {
  try {
    const snap = await getDoc(doc(db, 'roles', roleId));
    if (!snap.exists()) {
      throw new Error('Failed to fetch role');
    }
    return snap.data();
  } catch (error) {
    console.error('Error fetching role:', error);
    throw error;
  }
};
